// src/app/modules/AccessControl/pages/components/GenerateCodeModal.jsx

import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import * as actions from "../../../Dashbord/_redux/dashboard/Actions";
import {
  X, UserPlus, User, Phone, Mail, FileText,
  Calendar, Hash, AlertCircle, CheckCircle, Copy, Check, Users,
} from 'lucide-react';
import './GenerateCodeModal.scss';

const buildExtraGuest = () => ({ visitor_name: '', visitor_phone: '', visitor_email: '' });

const isValidPhone = (phone) => {
  if (!phone) return true; // optional field — empty is fine
  return /^\+?[1-9]\d{6,14}$/.test(phone.replace(/[\s\-().]/g, ''));
};


// Helper: format a Date to the value expected by <input type="datetime-local">
const toDatetimeLocal = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    date.getFullYear() + '-' +
    pad(date.getMonth() + 1) + '-' +
    pad(date.getDate()) + 'T' +
    pad(date.getHours()) + ':' +
    pad(date.getMinutes())
  );
};

const buildInitialForm = () => ({
  visitor_name: '',
  visitor_phone: '',
  visitor_email: '',
  // visitor_company: '',   // commented out — not required by sety intel
  purpose: '',
  // host_name: '',         // commented out — not required by sety intel
  // host_department: '',   // commented out — not required by sety intel
  valid_from: toDatetimeLocal(new Date()),   // defaults to user's current local time
  valid_until: '',
  max_uses: 1,
  // notes: '',             // commented out — not required by sety intel
  // space_name: '',        // commented out — not required by sety intel
  // space_id: '',          // commented out — not required by sety intel
});

export function GenerateCodeModal({ show, onHide, organizationId, onSuccess, getLabel }) {
  const dispatch = useDispatch();

  const [form, setForm] = useState(buildInitialForm);
  const [extraGuests, setExtraGuests] = useState([]);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [phoneError, setPhoneError] = useState('');

  // Success state: null | { code?: string, generated?: number }
  const [success, setSuccess] = useState(null);
  const [copied, setCopied] = useState(false);

  const isGroup = extraGuests.length > 0;
  const guestCount = 1 + extraGuests.length;

  // Re-seed valid_from to "now" each time the modal opens
  useEffect(() => {
    if (show) {
      setForm(buildInitialForm());
      setExtraGuests([]);
      setSendEmail(true);
      setSendSms(true);
      setError(null);
      setSuccess(null);
      setCopied(false);
    }
  }, [show]);

  const addGuest = () => setExtraGuests(g => [...g, buildExtraGuest()]);
  const removeGuest = (i) => setExtraGuests(g => g.filter((_, idx) => idx !== i));
  const setGuest = (i, key, val) => setExtraGuests(g => {
    const next = [...g];
    next[i] = { ...next[i], [key]: val };
    return next;
  });

  const set = (key) => (e) => {
    const raw = e && e.target ? e.target.value : e;
    const val = key === 'max_uses' ? Number(raw) : raw;
    setForm(f => ({ ...f, [key]: val }));
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value;
    setForm(f => ({ ...f, visitor_phone: val }));

    if (val && !isValidPhone(val)) {
      setPhoneError('Enter a valid international phone number (e.g. +1 555 0100)');
    } else {
      setPhoneError('');
    }
  };


  const handleClose = () => {
    if (loading) return;
    onHide();
  };

  const handleSubmit = async () => {
    if (!canSubmit || !organizationId) return;
    setLoading(true);
    setError(null);

    try {
      if (isGroup) {
        const validExtra = extraGuests.filter(g => g.visitor_name.trim());
        const result = await dispatch(actions.addData('generate_group_codes', {
          organization_id: organizationId,
          purpose:    form.purpose,
          valid_from: form.valid_from  || undefined,
          valid_until: form.valid_until,
          send_notifications: sendEmail || sendSms,
          visitors: [
            {
              visitor_name:  form.visitor_name.trim(),
              visitor_phone: form.visitor_phone || undefined,
              visitor_email: form.visitor_email || undefined,
            },
            ...validExtra.map(g => ({
              visitor_name:  g.visitor_name.trim(),
              visitor_phone: g.visitor_phone || undefined,
              visitor_email: g.visitor_email || undefined,
            })),
          ],
        }, organizationId));
        setSuccess({ generated: result?.data?.generated || guestCount });
        if (onSuccess) onSuccess(result);
      } else {
        const result = await dispatch(actions.addData('code', {
          organization_id: organizationId,
          visitor_name:  form.visitor_name.trim(),
          visitor_phone: form.visitor_phone  || undefined,
          visitor_email: form.visitor_email  || undefined,
          purpose:       form.purpose,
          valid_from:    form.valid_from  || undefined,
          valid_until:   form.valid_until,
          max_uses:      form.max_uses,
          send_email:    sendEmail,
          send_sms:      sendSms,
        }, organizationId));
        const code = result?.data?.code || result?.code || result?.access_code || null;
        setSuccess({ code });
        if (onSuccess) onSuccess(result);
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to generate code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (success?.code) {
      navigator.clipboard.writeText(success.code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const canSubmit =
    !loading &&
    form.visitor_name.trim().length > 0 &&
    form.purpose.trim().length >= 3 &&
    !!form.valid_until &&
    isValidPhone(form.visitor_phone); 

  // ── Success Screen ─────────────────────────────────────────────────────────
  if (success) {
    return (
      <Modal show={show} onHide={handleClose} centered size="md" className="generate-code-modal">
        <div>
          <div className="modal-header-custom">
            <div className="gcm-header-left">
              <div className="gcm-header-icon"><UserPlus size={16} /></div>
              <div className="gcm-header-text">
                <h3>Code Generated</h3>
                <p>Access code created successfully</p>
              </div>
            </div>
            <button className="btn-close-custom" onClick={handleClose}>
              <X size={15} />
            </button>
          </div>

          <div className="modal-body-custom">
            <div className="gcm-success">
              <div className="gcm-success__icon">
                <CheckCircle size={36} />
              </div>
              <p className="gcm-success__title">Access Code Ready</p>
              <p className="gcm-success__sub">
                {success.generated
                  ? `${success.generated} access codes have been generated and sent.`
                  : form.visitor_name
                    ? `A code has been generated for ${form.visitor_name}.`
                    : 'The access code has been generated.'}
                {!success.generated && ((sendEmail && form.visitor_email) || (sendSms && form.visitor_phone))
                  ? ' A notification has been sent to the visitor.'
                  : ''}
              </p>

              {success.code && (
                <div className="gcm-success__code-wrap">
                  <span className="gcm-success__code">{success.code}</span>
                  <button
                    className={`gcm-success__copy ${copied ? 'gcm-success__copy--done' : ''}`}
                    onClick={handleCopy}
                    title="Copy code"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer-custom">
            <button className="btn btn-primary btn-sm" onClick={handleClose}>
              Done
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // ── Main Form ──────────────────────────────────────────────────────────────
  return (
    <Modal show={show} onHide={handleClose} centered size="md" className="generate-code-modal">
      <div>
        {/* ── Header ── */}
        <div className="modal-header-custom">
          <div className="gcm-header-left">
            <div className="gcm-header-icon"><UserPlus size={16} /></div>
            <div className="gcm-header-text">
              <h3>Generate {getLabel ? getLabel('visitor') : 'Visitor'} Code</h3>
              <p>Create a time-limited access code for a visitor</p>
            </div>
          </div>
          <button className="btn-close-custom" onClick={handleClose} disabled={loading}>
            <X size={15} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="modal-body-custom">

          {error && (
            <div className="gcm-error-banner">
              <AlertCircle size={14} />
              <span>{error}</span>
              <button onClick={() => setError(null)}><X size={12} /></button>
            </div>
          )}

          {/* ── Visitor Info + extra guests — all in one section ── */}
          <div className="gcm-section">
            <div className="gcm-section__title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Visitor Information</span>
              {isGroup && (
                <span style={{ fontSize: 10, fontWeight: 700, color: '#5341C4', background: '#F0EEFF', padding: '2px 8px', borderRadius: 20 }}>
                  {guestCount} guests
                </span>
              )}
            </div>

            {/* Primary visitor */}
            <div className="gcm-row" style={{ marginBottom: 12 }}>
              <div className="gcm-field">
                <label><User size={12} />Full Name <span className="gcm-required">*</span></label>
                <input type="text" placeholder="e.g. Amara Okafor" value={form.visitor_name} onChange={set('visitor_name')} autoFocus />
              </div>
              <div className="gcm-field">
                <label><Phone size={12} />Phone Number</label>
                <input type="tel" placeholder="+2348110262879" value={form.visitor_phone} onChange={handlePhoneChange} />
                {phoneError && <span className="gcm-field__error">{phoneError}</span>}
              </div>
            </div>
            <div className="gcm-row">
              <div className="gcm-field">
                <label><Mail size={12} />Email Address</label>
                <input type="email" placeholder="visitor@example.com" value={form.visitor_email} onChange={set('visitor_email')} />
              </div>
            </div>

            {/* Additional guests */}
            {extraGuests.map((g, i) => (
              <div key={i} style={{ borderTop: '1px solid #F0F0F5', marginTop: 12, paddingTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                    Guest {i + 2}
                  </span>
                  <button
                    onClick={() => removeGuest(i)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#CBD5E1', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3, padding: 0 }}
                    onMouseOver={e => e.currentTarget.style.color = '#DC2626'}
                    onMouseOut={e => e.currentTarget.style.color = '#CBD5E1'}
                  >
                    <X size={12} /> Remove
                  </button>
                </div>
                <div className="gcm-row">
                  <div className="gcm-field">
                    <input type="text" placeholder="Full name *" value={g.visitor_name} onChange={e => setGuest(i, 'visitor_name', e.target.value)} />
                  </div>
                  <div className="gcm-field">
                    <input type="tel" placeholder="Phone (optional)" value={g.visitor_phone} onChange={e => setGuest(i, 'visitor_phone', e.target.value)} />
                  </div>
                </div>
              </div>
            ))}

            {/* Add guest link — sits flush inside the section */}
            <div style={{ borderTop: '1px solid #F0F0F5', marginTop: 12, paddingTop: 10 }}>
              <button
                onClick={addGuest}
                type="button"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5341C4', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, padding: 0 }}
              >
                <span style={{ width: 18, height: 18, borderRadius: '50%', border: '1.5px solid #5341C4', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, lineHeight: 1 }}>+</span>
                Add another guest
              </button>
            </div>
          </div>

          {/* ── Visit Details ── */}
          <div className="gcm-section">
            <div className="gcm-section__title">Visit Details</div>

            <div className="gcm-row" style={{ marginBottom: 12 }}>
              <div className="gcm-field">
                <label><FileText size={12} />Purpose <span className="gcm-required">*</span></label>
                <select value={form.purpose} onChange={set('purpose')}>
                  <option value="">Select purpose…</option>
                  <option value="Meeting">Meeting</option>
                  <option value="Delivery">Delivery</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Personal">Personal</option>
                  <option value="Interview">Interview</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              {/* space / location — commented out, not required by sety intel */}
              {/* <div className="gcm-field">
                <label><MapPin size={12} />Space / Location</label>
                <input
                  type="text"
                  placeholder="e.g. Board Room, Floor 3"
                  value={form.space_name}
                  onChange={set('space_name')}
                />
              </div> */}
            </div>

            {/* host_name + host_department — commented out, not required by sety intel */}
            {/* <div className="gcm-row" style={{ marginBottom: 12 }}>
              <div className="gcm-field">
                <label><User size={12} />Host Name</label>
                <input
                  type="text"
                  placeholder="Who are they visiting?"
                  value={form.host_name}
                  onChange={set('host_name')}
                />
              </div>
              <div className="gcm-field">
                <label><Building2 size={12} />Host Department</label>
                <input
                  type="text"
                  placeholder="e.g. Engineering, HR"
                  value={form.host_department}
                  onChange={set('host_department')}
                />
              </div>
            </div> */}

            <div className="gcm-row">
              <div className="gcm-field">
                <label><Calendar size={12} />Valid From</label>
                <input
                  type="datetime-local"
                  value={form.valid_from}
                  onChange={set('valid_from')}
                />
              </div>
              <div className="gcm-field">
                <label><Calendar size={12} />Valid Until <span className="gcm-required">*</span></label>
                <input
                  type="datetime-local"
                  value={form.valid_until}
                  onChange={set('valid_until')}
                  min={form.valid_from || undefined}
                />
              </div>
            </div>
          </div>

          {/* ── Access Config ── */}
          <div className="gcm-section">
            <div className="gcm-section__title">Access Configuration</div>
            <div className="gcm-row">
              <div className="gcm-field">
                <label><Hash size={12} />Max Uses</label>
                <select value={form.max_uses} onChange={set('max_uses')}>
                  <option value={1}>1 — Single entry</option>
                  <option value={2}>2 uses</option>
                  <option value={3}>3 uses</option>
                  <option value={5}>5 uses</option>
                </select>
              </div>
            </div>
          </div>

          {/* notes — commented out, not required by sety intel */}
          {/* <div className="gcm-section">
            <div className="gcm-section__title">Notes</div>
            <div className="gcm-field">
              <label><FileText size={12} />Additional Notes</label>
              <textarea
                placeholder="Any extra information for security staff…"
                value={form.notes}
                onChange={set('notes')}
                rows={3}
              />
            </div>
          </div> */}

          {/* ── Notify Visitor ── */}
          <div className="gcm-section">
            <div className="gcm-section__title">Notify Visitor</div>
            <div className="gcm-notify">

              <div
                className={[
                  'gcm-notify__item',
                  sendEmail ? 'gcm-notify__item--active' : '',
                  !form.visitor_email ? 'gcm-notify__item--disabled' : '',
                ].join(' ')}
                onClick={() => form.visitor_email && setSendEmail(v => !v)}
              >
                <div className="gcm-notify__left">
                  <Mail size={15} />
                  <div>
                    <div className="gcm-notify__label">Send Email</div>
                    <div className="gcm-notify__hint">
                      {form.visitor_email
                        ? `Code will be sent to ${form.visitor_email}`
                        : 'Add an email address above to enable'}
                    </div>
                  </div>
                </div>
                <div className={`gcm-notify__toggle ${sendEmail && form.visitor_email ? 'gcm-notify__toggle--on' : ''}`} />
              </div>

              <div
                className={[
                  'gcm-notify__item',
                  sendSms ? 'gcm-notify__item--active' : '',
                  !form.visitor_phone ? 'gcm-notify__item--disabled' : '',
                ].join(' ')}
                onClick={() => form.visitor_phone && setSendSms(v => !v)}
              >
                <div className="gcm-notify__left">
                  <Mail size={15} />
                  <div>
                    <div className="gcm-notify__label">Send SMS</div>
                    <div className="gcm-notify__hint">
                      {form.visitor_phone
                        ? `Code will be sent to ${form.visitor_phone}`
                        : 'Add a phone number above to enable'}
                    </div>
                  </div>
                </div>
                <div className={`gcm-notify__toggle ${sendSms && form.visitor_phone ? 'gcm-notify__toggle--on' : ''}`} />
              </div>

            </div>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="modal-footer-custom">
          <button className="sety-btn sety-btn-ghost" onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {loading ? 'Generating…' : isGroup ? `Generate ${guestCount} Codes` : 'Generate Code'}
          </button>
        </div>
      </div>
    </Modal>
  );
}